import { useCallback, useRef, useState } from 'react';
import { apiDownloadBlob, apiPostForm } from '../api';
import { getDisplayError } from '../apiErrors';
import { unwrapData } from '@wapharma/shared';
import { getToken } from '../auth';
import { isImportFile } from './useProductBulkImport';

export type TaxonomyFacetId = 'markes' | 'product-types' | 'categories' | 'tags';

export type TaxonomyImportRowStatus = 'ready' | 'invalid' | 'exists' | 'skipped';

export type TaxonomyFacetPreviewRow = {
  row: number;
  status: TaxonomyImportRowStatus;
  issues: string[];
  name?: string;
  slug?: string;
  code?: string;
  label?: string;
  description?: string | null;
  parentSlug?: string | null;
  familyName?: string;
  familyCode?: string;
  tagLabel?: string;
  tagValue?: string;
};

export type TaxonomyFacetPreview = {
  rowCount: number;
  summary: {
    ready: number;
    invalid: number;
    exists: number;
    skipped: number;
  };
  rows: TaxonomyFacetPreviewRow[];
  canCommit: boolean;
};

export type TaxonomyFacetCommitResult = {
  created: Array<{
    row: number;
    id?: string;
    name?: string;
    slug?: string;
    code?: string;
    label?: string;
    parentId?: string | null;
    familyId?: string;
    familyName?: string;
    value?: string;
  }>;
  errors: Array<{ row: number; message: string }>;
};

const FACET_META: Record<
  TaxonomyFacetId,
  { basePath: string; templateBase: string }
> = {
  markes: {
    basePath: '/catalog/admin/markes/import',
    templateBase: 'modele-import-marques',
  },
  'product-types': {
    basePath: '/catalog/admin/product-types/import',
    templateBase: 'modele-import-types-produit',
  },
  categories: {
    basePath: '/catalog/admin/categories/import',
    templateBase: 'modele-import-categories',
  },
  tags: {
    basePath: '/catalog/admin/tags/import',
    templateBase: 'modele-import-tags',
  },
};

export async function downloadTaxonomyFacetImportTemplate(
  token: string,
  facet: TaxonomyFacetId,
  format: 'xlsx' | 'csv',
) {
  const meta = FACET_META[facet];
  const filename = `${meta.templateBase}.${format}`;
  await apiDownloadBlob(`${meta.basePath}/template?format=${format}`, token, filename);
}

export function useTaxonomyFacetBulkImport(
  facet: TaxonomyFacetId,
  opts?: { onCommitted?: (result: TaxonomyFacetCommitResult) => void },
) {
  const token = getToken();
  const fileRef = useRef<HTMLInputElement>(null);
  const fileStateRef = useRef<File | null>(null);
  const onCommittedRef = useRef(opts?.onCommitted);
  onCommittedRef.current = opts?.onCommitted;
  const basePath = FACET_META[facet].basePath;

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<TaxonomyFacetPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [pickErr, setPickErr] = useState<string | null>(null);
  const [commitResult, setCommitResult] = useState<TaxonomyFacetCommitResult | null>(null);

  const reset = useCallback(() => {
    fileStateRef.current = null;
    setFile(null);
    setPreview(null);
    setErr(null);
    setPickErr(null);
    setCommitResult(null);
    setPreviewLoading(false);
    setCommitting(false);
    if (fileRef.current) fileRef.current.value = '';
  }, []);

  const runPreview = useCallback(
    async (next: File) => {
      if (!token) {
        setErr('Session expirée. Reconnectez-vous pour analyser le fichier.');
        setPreview(null);
        return;
      }
      setPreviewLoading(true);
      setErr(null);
      setCommitResult(null);
      try {
        const form = new FormData();
        form.append('file', next);
        const raw = await apiPostForm<{ data: TaxonomyFacetPreview }>(
          `${basePath}/preview`,
          form,
          token,
        );
        setPreview(unwrapData(raw) as TaxonomyFacetPreview);
      } catch (e: unknown) {
        setPreview(null);
        setErr(getDisplayError(e));
      } finally {
        setPreviewLoading(false);
      }
    },
    [token, basePath],
  );

  const applyFile = useCallback(
    (next: File | null) => {
      setPickErr(null);
      setErr(null);
      setCommitResult(null);
      setPreview(null);
      if (!next) {
        fileStateRef.current = null;
        setFile(null);
        if (fileRef.current) fileRef.current.value = '';
        return;
      }
      if (!isImportFile(next)) {
        setPickErr('Format accepté : .xlsx, .xls ou .csv');
        fileStateRef.current = null;
        setFile(null);
        return;
      }
      fileStateRef.current = next;
      setFile(next);
      void runPreview(next);
    },
    [runPreview],
  );

  const commit = useCallback(async () => {
    const current = fileStateRef.current;
    if (!current) {
      setErr('Fichier requis.');
      return;
    }
    if (!token) {
      setErr('Session expirée.');
      return;
    }
    setCommitting(true);
    setErr(null);
    try {
      const form = new FormData();
      form.append('file', current);
      const raw = await apiPostForm<{ data: TaxonomyFacetCommitResult }>(
        `${basePath}/commit`,
        form,
        token,
      );
      const result = unwrapData(raw) as TaxonomyFacetCommitResult;
      setCommitResult(result);
      onCommittedRef.current?.(result);
    } catch (e: unknown) {
      setErr(getDisplayError(e));
    } finally {
      setCommitting(false);
    }
  }, [token, basePath]);

  const busy = previewLoading || committing;
  const zoneDisabled = !token || busy;
  const canCommit = Boolean(preview?.canCommit) && !busy && !commitResult;

  return {
    facet,
    token,
    fileRef,
    file,
    preview,
    previewLoading,
    committing,
    busy,
    err,
    pickErr,
    commitResult,
    zoneDisabled,
    canCommit,
    reset,
    applyFile,
    commit,
  };
}
